// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The shared vocabulary the playfield's furniture is built from (M3-05).
//
// Rails, posts and mechanisms are all the same handful of shapes in the same handful of
// finishes, so they share one palette and one set of builders. That is not tidiness: sixty
// meshes each carrying its own `MeshStandardMaterial` is sixty shader programs and sixty
// uniform uploads a frame, and every one of them looks subtly different from the next because
// nobody types the same roughness twice.
//
// The palette is owned by whoever creates it and handed to the builders, so a scene disposes
// exactly the materials it made. Three does not reference-count; a material dropped without
// `dispose()` keeps its program and its textures on the GPU until the context goes away.

import {
  BufferAttribute,
  BufferGeometry,
  CylinderGeometry,
  DoubleSide,
  Mesh,
  MeshPhysicalMaterial,
  MeshStandardMaterial,
  type Material,
} from 'three'
import type { MaterialName } from '@core/sim/materials'
import bumperFinish from '../../SourceArt/PierHardware/finish-06.json'
import { railTriangles, type Point2 } from './railPath'
import {createBallMaterials} from './balls'
import {makeGuideBallReflection,updateGuideBallReflection} from './guideBallReflections'
import type {Group} from 'three'
import {toyOpticalSurface} from './toyOptics'
import {acrylicSurface} from './acrylicSurface'

let slingMaker:((key:string)=>MeshStandardMaterial)|undefined
/** Assembly preparation registers its deferred material factory before any part is built. */
export function registerSlingSurfaces(factory:(key:string)=>MeshStandardMaterial):void {slingMaker=factory}

/** Finishes the playfield's furniture comes in: the machine's own material names, plus parts. */
export type Finish =
  | MaterialName
  | 'chrome'
  | 'coilArm'
  | 'targetFace'
  | 'lampLens'
  | 'shadow'
  | 'rampPlastic'
  // The cabinet's own. Here rather than inline in `cabinet.ts` and `glass.ts` so that the machine
  // has exactly one palette — and so the parity guard sees all of it, not the playfield's half.
  | 'playfieldArt'
  | 'cabinetBody'
  | 'cabinetTrim'
  | 'cabinetDark'
  | 'backglassArt'
  | 'playfieldGlass'
  // The editor's three (M13-18): a holographic plinth, the part in hand, the part under a fingertip.
  | 'hologram'
  | 'ghost'
  | 'selected'
  // …and the fourth (M13-13): the snap guides — the 2-D canvas's snap pink, on the bench.
  | 'guide'
  | 'printedPlastic'
  | 'printedCabinet'
  | 'clearRamp'
  | 'hardwareSteel'
  | 'hardwareRubber'
  | 'legSatinSteel'
  | 'legFastenerSteel'
  | 'legFootRubber'
  | 'hullPaint' | 'industrialPaint' | 'signalAmber' | 'signalTeal'
  | 'hardwareIvory'
  | 'hardwareMark'
  | 'rampClearSheet'
  | 'rampClearEdge'
  | 'navDeckSheet'

/**
 * One material per finish, created once and disposed once.
 *
 * The colours are NEON NOVA's — cold chrome, hot pink rubber, cyan plastics — because the
 * flagship is the only machine there is. A machine will eventually state its own furniture
 * palette in its definition; until it does, these are honest defaults rather than a guess
 * dressed up as a feature.
 */
export class PartPalette {
  constructor(private readonly rubberColour?: string) {}
  private readonly materials = new Map<Finish, Material>()
  private readonly acrylicSurfaces = new Map<Finish, MeshStandardMaterial>()
  acrylic(finish:Finish):MeshStandardMaterial {
    const prior=this.acrylicSurfaces.get(finish)
    if(prior!==undefined)return prior
    const made=acrylicSurface(finish,FINISHES[finish]);this.acrylicSurfaces.set(finish,made);return made
  }
  private reflectedGuide:MeshStandardMaterial|undefined
  guideSteel(source?:Material):MeshStandardMaterial {return this.reflectedGuide??=makeGuideBallReflection(source??this.get('hardwareSteel'))}
  guideBalls(group:Group|undefined):void {if(this.reflectedGuide)updateGuideBallReflection(this.reflectedGuide,group)}
  private clearGuard:MeshStandardMaterial|undefined
  opticalGuard():MeshStandardMaterial{
    if(this.clearGuard===undefined){this.clearGuard=toyOpticalSurface('guard',.075);this.clearGuard.color.setRGB(.45*.07,.55*.07,.68*.07)}
    return this.clearGuard
  }
  private captiveSteel:readonly Material[]|undefined
  /** Shared polished sphere finish, owned/disposed with the static furniture palette. */
  captiveBallSteel():Material {this.captiveSteel??=createBallMaterials();return this.captiveSteel[0]!}
  private flipperPolymer: MeshStandardMaterial | undefined
  private postPolymer: MeshStandardMaterial | undefined
  private spinnerSteel: MeshStandardMaterial | undefined
  private bumperSteel: MeshStandardMaterial | undefined
  private bumperRing: MeshStandardMaterial | undefined
  private bumperSkirt: MeshStandardMaterial | undefined
  private readonly slingSurfaces = new Map<string, MeshStandardMaterial>()
  slingSurface(key: string): MeshStandardMaterial {
    const prior = this.slingSurfaces.get(key)
    if (prior !== undefined) return prior
    if(slingMaker===undefined)throw new Error('Sling assembly surfaces have not been prepared')
    const material=slingMaker(key)
    this.slingSurfaces.set(key,material); return material
  }

  get(finish: Finish): Material {
    if(finish==='navDeckSheet')return this.acrylic(finish)
    const existing = this.materials.get(finish)
    if (existing !== undefined) return existing
    const made = make(finish, (finish === 'rubber' || finish === 'flipperRubber') ? this.rubberColour : undefined)
    this.materials.set(finish, made)
    return made
  }

  /** A private satin dielectric for fitted new flippers; shared ivory stays unchanged. */
  satinFlipperPolymer(): MeshStandardMaterial {
    this.flipperPolymer ??= new MeshStandardMaterial({
      name: 'flipper.satinPolymer', color: FINISHES.hardwareIvory.color,
      roughness: 0.30, metalness: 0, envMapIntensity: 1,
    })
    return this.flipperPolymer
  }

  /** Rough molded nylon, isolated from accepted flipper and ordinary ivory finishes. */
  satinPostPolymer(): MeshStandardMaterial {
    this.postPolymer ??= new MeshStandardMaterial({
      name: 'post.satinPolymer', color: FINISHES.hardwareIvory.color,
      roughness: 0.70, metalness: 0, envMapIntensity: 1,
    })
    return this.postPolymer
  }

  /** Smooth turned conductor finish for fitted new spinners only. */
  satinSpinnerSteel(): MeshStandardMaterial {
    this.spinnerSteel ??= new MeshStandardMaterial({
      name: 'spinner.satinSteel', color: 0xcdd1d6, roughness: 0.50, metalness: 1, envMapIntensity: 1,
    })
    return this.spinnerSteel
  }

  /** Private coatings only requested by the fitted new-machine bumper family. */
  bumperSurface(finish: Finish): Material {
    if (finish === 'hardwareSteel') return this.bumperSteel ??= new MeshStandardMaterial({name:'bumper.turnedSteel', ...bumperFinish.steel})
    if (finish === 'lampLens') {
      const {boardIntensity: _boardIntensity,...surface}=bumperFinish.ring
      return this.bumperRing ??= new MeshStandardMaterial({name:'bumper.recessedLens', ...surface})
    }
    if (finish !== 'hardwareRubber') return this.get(finish)
    if (this.bumperSkirt !== undefined) return this.bumperSkirt
    const f=bumperFinish.skirt
    const made=new MeshStandardMaterial({name:'bumper.skirt',color:f.color,roughness:f.roughness,metalness:f.metalness,
      emissive:f.edgeColor,emissiveIntensity:f.edgeIntensity})
    made.onBeforeCompile=shader=>{
      shader.vertexShader=shader.vertexShader.replace('#include <common>', '#include <common>\nvarying float bumperEdge;')
        .replace('#include <begin_vertex>', '#include <begin_vertex>\nbumperEdge = uv.y;')
      shader.fragmentShader=shader.fragmentShader.replace('#include <common>', '#include <common>\nvarying float bumperEdge;')
        .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\nfloat edgeFalloff=1.0-clamp(bumperEdge,0.0,1.0);\ntotalEmissiveRadiance *= edgeFalloff * edgeFalloff;')
    }
    made.customProgramCacheKey=()=> 'bumper-base-response06'
    return this.bumperSkirt=made
  }

  /** Every material handed out so far, for the scene that owns them to dispose. */
  all(): readonly Material[] {
    return [...this.materials.values(), ...this.acrylicSurfaces.values(), ...(this.flipperPolymer === undefined ? [] : [this.flipperPolymer]),
      ...(this.postPolymer === undefined ? [] : [this.postPolymer]),
      ...(this.spinnerSteel === undefined ? [] : [this.spinnerSteel]),
        ...[this.bumperSteel,this.bumperRing,this.bumperSkirt,this.clearGuard].filter((m):m is MeshStandardMaterial=>m!==undefined),...this.slingSurfaces.values(),...(this.captiveSteel??[])]
  }

  dispose(): void {
    for (const m of this.materials.values()) m.dispose()
    this.materials.clear()
    for(const m of this.acrylicSurfaces.values())m.dispose()
    this.acrylicSurfaces.clear()
    this.flipperPolymer?.dispose()
    this.flipperPolymer = undefined
    this.postPolymer?.dispose()
    this.postPolymer = undefined
    this.spinnerSteel?.dispose()
    this.spinnerSteel = undefined
    for(const m of [this.bumperSteel,this.bumperRing,this.bumperSkirt])m?.dispose()
    this.bumperSteel=this.bumperRing=this.bumperSkirt=undefined
    for(const m of this.slingSurfaces.values())m.dispose()
    this.slingSurfaces.clear()
    this.captiveSteel?.forEach(m=>m.dispose());this.captiveSteel=undefined
    this.clearGuard?.dispose();this.clearGuard=undefined
    this.reflectedGuide?.dispose();this.reflectedGuide=undefined
  }
}

/**
 * A finish's numbers, as data rather than as a call.
 *
 * **This table is compared against the native client's, by `scripts/check-finish-parity.mjs`.**
 * The two clients are two presentations of one product, and the same NEON NOVA with cold chrome
 * in a browser and warm pewter in a headset is the kind of difference nobody reports as a bug and
 * everybody notices. `Assets/HccPinball/App/Render/Finish.cs` holds Unity's copy; neither can be
 * generated from the other, because one is compiled by Vite and the other by Unity, so a guard is
 * the price. It is why these are values in a table and not arguments buried in a `switch`.
 */
export interface FinishNumbers {
  readonly color: number
  readonly roughness: number
  readonly metalness: number
  readonly emissive?: number
  readonly emissiveIntensity?: number
  readonly opacity?: number
  readonly depthWrite?: boolean
  readonly doubleSided?: boolean
  /**
   * A second specular lobe. **Browser only** — URP pays for clearcoat on every fragment of a
   * tile-based GPU, so the native client folds it into a lower roughness instead. The parity
   * guard knows that, and allows the two roughnesses to differ only where this is set.
   */
  readonly clearcoat?: number
  readonly clearcoatRoughness?: number
  /** Real refraction. Browser only, for the same reason, and it needs a framebuffer copy. */
  readonly transmission?: number
  readonly thickness?: number
}

export const FINISHES: Readonly<Record<Finish, FinishNumbers>> = {
  hardwareSteel: { color: 0xd5d9de, roughness: 0.32, metalness: 0.65 },
  hardwareRubber: { color: 0x393d41, roughness: 0.65, metalness: 0 },
  legSatinSteel: { color: 0xc4cace, roughness: 0.29, metalness: 0.94 },
  legFastenerSteel: { color: 0xd3d9dd, roughness: 0.19, metalness: 0.95 },
  hullPaint: { color: 0xffffff, roughness: 0.54, metalness: 0.65 },
  industrialPaint: { color: 0x223944, roughness: 0.50, metalness: 0.75 },
  signalAmber: { color: 0xe5a239, roughness: 0.50, metalness: 0.10, emissive: 0xe5a239, emissiveIntensity: 0.30 },
  signalTeal: { color: 0x38d6d1, roughness: 0.50, metalness: 0.10, emissive: 0x38d6d1, emissiveIntensity: 0.30 },
  legFootRubber: { color: 0x2e3236, roughness: 0.70, metalness: 0 },
  hardwareIvory: { color: 0xdedbd3, roughness: 0.46, metalness: 0, emissive: 0xdedbd3, emissiveIntensity: 0.06 },
  hardwareMark: { color: 0xdad9cc, roughness: 0.74, metalness: 0, emissive: 0xdad9cc, emissiveIntensity: 0.12 },
  clearRamp: { color: 0xecf4ff, roughness: 0.10, metalness: 0, opacity: 0.20, depthWrite: false, doubleSided: true },
  // Closed molded sheets already include front/back faces. Single-sided blending avoids
  // repeated alpha overdraw; thin rims retain a stronger edge response.
  rampClearSheet: { color: 0xf1f5fa, roughness: 0.14, metalness: 0, opacity: 0.085, depthWrite: false },
  rampClearEdge: { color: 0xf1f5fa, roughness: 0.14, metalness: 0, opacity: 0.26, depthWrite: false },
  // Owner adjustment: ten percentage points less transparent on the Nav roof only.
  navDeckSheet: { color: 0xf1f5fa, roughness: 0.14, metalness: 0, opacity: 0.185, depthWrite: false },
  metal: { color: 0xb9c4d6, roughness: 0.28, metalness: 0.92 },
  wire: { color: 0xb9c4d6, roughness: 0.28, metalness: 0.92 },
  chrome: { color: 0xe4ecf8, roughness: 0.1, metalness: 1, clearcoat: 0.5 },
  // Pink rubber. A machine's rubbers are the one part a player's eye tracks without being told
  // to, because they are the only saturated thing at ball height.
  rubber: { color: 0xff2f7a, roughness: 0.82, metalness: 0.02 },
  flipperRubber: { color: 0xff2f7a, roughness: 0.82, metalness: 0.02 },
  // Opaque, with a clearcoat doing the work a real playfield plastic's translucency does.
  // `transmission` would be closer to the truth and costs a whole extra render pass plus a
  // sorting problem: thirteen overlapping transparent guides draw in whatever order the scene
  // graph happens to hold them, so a lane rail flickers in front of a wall it is behind, and it
  // does so only from some angles. A lit, glossy solid reads as plastic from every angle.
  plastic: { color: 0x8fe4ff, roughness: 0.26, metalness: 0.02, clearcoat: 0.7, clearcoatRoughness: 0.15 },
  glass: {
    color: 0xffffff,
    roughness: 0.05,
    metalness: 0,
    transmission: 0.92,
    thickness: 0.002,
    opacity: 0.25,
    depthWrite: false,
  },
  wood: { color: 0x2b2f45, roughness: 0.7, metalness: 0.05 },
  coilArm: { color: 0x8d97ab, roughness: 0.45, metalness: 0.7 },
  // Lit from within, because a drop target's face is a lamp behind a coloured lens.
  targetFace: {
    color: 0xffd166,
    roughness: 0.35,
    metalness: 0.05,
    emissive: 0xff8a1f,
    emissiveIntensity: 0.55,
  },
  lampLens: {
    color: 0x1a2338,
    roughness: 0.3,
    metalness: 0.1,
    emissive: 0x4fd8ff,
    emissiveIntensity: 0.9,
  },
  shadow: { color: 0x05070e, roughness: 0.95, metalness: 0 },
  // The one place transparency is worth its cost. An opaque ramp hides the art under it, hides
  // the ball on the wood beneath it, and turns the busiest part of the machine into a blank
  // white slab. Two ramps of six meshes each is a small enough set for the sorting to behave,
  // and `depthWrite: false` stops the near wall from punching a hole in the far one.
  rampPlastic: {
    color: 0xbfefff,
    roughness: 0.12,
    metalness: 0,
    clearcoat: 0.9,
    clearcoatRoughness: 0.06,
    opacity: 0.34,
    depthWrite: false,
    doubleSided: true,
  },

  // --- the cabinet ------------------------------------------------------------------
  // White, because the whole colour comes from the art. A playfield with no art of its own is
  // drawn in `UNPAINTED_PLAYFIELD` instead — a bare surface, not a white one.
  playfieldArt: { color: 0xffffff, roughness: 0.22, metalness: 0.02 },
  cabinetBody: { color: 0x0a0a12, roughness: 0.55, metalness: 0.05 },
  cabinetTrim: { color: 0xb9c2d4, roughness: 0.25, metalness: 0.9 },
  cabinetDark: { color: 0x05050a, roughness: 0.8, metalness: 0 },
  // White, because the whole colour comes from the art. Emissive from the same map, so a
  // backglass is lit from behind the way a real one is rather than by a lamp that would also
  // light the wall behind the machine.
  backglassArt: { color: 0xffffff, roughness: 0.35, metalness: 0, emissive: 0xffffff, emissiveIntensity: 0.85 },
  // Nearly perfectly smooth: a sheet of float glass has no texture at all, and any roughness
  // blurs the reflection into a haze that reads as dirt rather than as glass. Very low opacity,
  // because this is *only* the reflection — a real sheet also tints and refracts what is under
  // it, and modelling that would mean a transmission pass over the whole playfield.
  playfieldGlass: { color: 0xffffff, roughness: 0.02, metalness: 0, opacity: 0.055, depthWrite: false },

  // --- the editor (M13-18) -------------------------------------------------------------
  // Drawn unlit on both clients — a hologram is its own light — so roughness and metalness are
  // the table's convention rather than anything the shader reads; colour and opacity are what
  // the two trays, ghosts and halos have to agree on, and `check:finishes` holds them to it.
  // The plinth a part stands on in the tray, and its rail: the tray's blue at a quarter.
  hologram: { color: 0x3aa0e0, roughness: 1, metalness: 0, opacity: 0.25, depthWrite: false, doubleSided: true },
  // The part in hand, where it would land: pale, see-through, the tray's halo blue at half.
  ghost: { color: 0x9fd8ff, roughness: 1, metalness: 0, opacity: 0.45, depthWrite: false },
  // The placed part under a fingertip — the one a pinch would take — read through everything.
  selected: { color: 0x66e0ff, roughness: 1, metalness: 0, opacity: 0.4, depthWrite: false },
  // The snap guides (M13-13): the line to a partner, the centre line, the marks — `paint.ts`'s
  // `snapLine` pink, so a snap on the bench is the same colour as a snap on the canvas.
  guide: { color: 0xff7ab6, roughness: 1, metalness: 0, opacity: 0.9, depthWrite: false },
  printedPlastic: { color: 0xffffff, roughness: 0.22, metalness: 0.02 },
  printedCabinet: { color: 0xffffff, roughness: 0.45, metalness: 0.05 },
}

/**
 * A material from a finish's numbers.
 *
 * `MeshPhysicalMaterial` only where a finish asks for something a standard one cannot do, because
 * the physical shader is a strictly more expensive program and eight of these thirteen do not
 * need it.
 */
function make(finish: Finish, colour?: string): Material {
  const numbers = FINISHES[finish]
  const physical =
    numbers.clearcoat !== undefined ||
    numbers.transmission !== undefined
  const common = {
    color: colour ?? numbers.color,
    roughness: numbers.roughness,
    metalness: numbers.metalness,
    ...(numbers.emissive === undefined ? {} : { emissive: numbers.emissive }),
    ...(numbers.emissiveIntensity === undefined ? {} : { emissiveIntensity: numbers.emissiveIntensity }),
    ...(numbers.opacity === undefined ? {} : { transparent: true, opacity: numbers.opacity }),
    ...(numbers.depthWrite === undefined ? {} : { depthWrite: numbers.depthWrite }),
    ...(numbers.doubleSided === true ? { side: DoubleSide } : {}),
  }
  if (!physical) return new MeshStandardMaterial(common)
  return new MeshPhysicalMaterial({
    ...common,
    ...(numbers.clearcoat === undefined ? {} : { clearcoat: numbers.clearcoat }),
    ...(numbers.clearcoatRoughness === undefined ? {} : { clearcoatRoughness: numbers.clearcoatRoughness }),
    ...(numbers.transmission === undefined ? {} : { transmission: numbers.transmission }),
    ...(numbers.thickness === undefined ? {} : { thickness: numbers.thickness }),
  })
}

/** The bare wood a playfield is drawn in when its machine states no art for the surface. */
export const UNPAINTED_PLAYFIELD = 0x121a2c

/** How tall a guide in each finish stands above the wood, metres. */
export const RAIL_HEIGHT: Readonly<Record<MaterialName, number>> = {
  // Above a ball's 27 mm diameter: a lane guide the ball cannot ride over.
  metal: 0.032,
  wire: 0.028,
  // A plastic guide sits lower and the ball can graze its top edge, which is true of the real
  // thing and is why plastics get chipped.
  plastic: 0.024,
  rubber: 0.02,
  flipperRubber: 0.02,
  glass: 0.024,
  // The shooter lane's floor is a lip, not a wall.
  wood: 0.008,
}

/** How thick a guide is, metres. Half of this stands each side of the stated centre-line. */
export const RAIL_HALF_WIDTH = 0.0022

/** A guide, as a mesh in playfield coordinates. The caller owns the geometry it returns. */
export function railMesh(
  points: readonly Point2[],
  material: Material,
  height: number,
  halfWidth = RAIL_HALF_WIDTH,
  base = 0,
): Mesh {
  const geometry = new BufferGeometry()
  const positions = railTriangles(points, halfWidth, base, height)
  geometry.setAttribute('position', new BufferAttribute(positions, 3))
  geometry.computeVertexNormals()
  geometry.computeBoundingSphere()
  return new Mesh(geometry, material)
}

/**
 * A cylinder standing on the playfield, axis along +z.
 *
 * Three builds a cylinder along its own +y, so every upright in the machine would otherwise
 * carry the same `rotateX(π/2)` written out by hand, and one of them would eventually be
 * written `−π/2`. `radialSegments` is low by default: a 9.5 mm post is a few pixels across
 * from the player's camera and twelve sides is already more than anyone can resolve.
 */
export function uprightCylinder(
  radiusTop: number,
  radiusBottom: number,
  height: number,
  base: number,
  radialSegments = 12,
): BufferGeometry {
  const geometry = new CylinderGeometry(radiusTop, radiusBottom, height, radialSegments, 1, false)
  geometry.rotateX(Math.PI / 2)
  geometry.translate(0, 0, base + height / 2)
  return geometry
}

/** What `postMeshes` built: meshes to add, and the geometries whoever added them must dispose. */
export interface BuiltMeshes {
  readonly meshes: readonly Mesh[]
  readonly geometries: readonly BufferGeometry[]
}

/**
 * A post: a steel core in a rubber sleeve, which is what the collider's radius describes.
 *
 * The collider is a circle of the *sleeve's* radius — the ball never touches the core — so
 * the sleeve is drawn at exactly the stated radius and the core inside it. Drawing the core
 * at the collider radius is the mistake that makes a machine look right and play wrong: the
 * ball bounces two millimetres off the metal it appears to touch.
 */
export function postMeshes(radius: number, finish: MaterialName, palette: PartPalette): BuiltMeshes {
  const meshes: Mesh[] = []
  const geometries: BufferGeometry[] = []

  if (finish === 'rubber' || finish === 'flipperRubber') {
    const sleeve = uprightCylinder(radius, radius, 0.024, 0.004)
    geometries.push(sleeve)
    meshes.push(new Mesh(sleeve, palette.get('rubber')))

    const core = uprightCylinder(radius * 0.45, radius * 0.5, 0.038, 0)
    geometries.push(core)
    meshes.push(new Mesh(core, palette.get('chrome')))

    const cap = uprightCylinder(radius * 0.72, radius * 0.62, 0.003, 0.038)
    geometries.push(cap)
    meshes.push(new Mesh(cap, palette.get('chrome')))
  } else {
    const body = uprightCylinder(radius * 0.9, radius, 0.034, 0)
    geometries.push(body)
    meshes.push(new Mesh(body, palette.get(finish)))
  }

  return { meshes, geometries }
}
